import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useForm, useFieldArray } from 'react-hook-form';
import { array, object } from 'yup';
import { formResolver } from 'src/utils/formResolver';
import { booleanScheme, numberScheme, stringScheme, urlScheme } from 'src/utils/validation-schemes';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
} from 'src/services/knowledgeBaseService';
import {
  WEBSITE_INTERVAL,
  WEBSITE_INITIAL_REQUEST,
  WEBSITE_INITIAL_REQUEST_V2,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { useAppSelector } from 'src/store';
import { API_VERSION } from 'src/constants/api-routes';
import { replaceSpecialSymbols } from 'src/helpers/replaceSpecialSymbols';
import { generateUniqueName } from 'src/utils/fileUtils';
import type { CollectorInitialRequest } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/integrationFormProps';
import type { WebsiteFormValues } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/websiteFormValues';
import { WebsiteCollectorFields } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/WebsiteCollectorFields/WebsiteCollectorFields';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { getApiVersion } from 'src/utils/apiVersion';

export const CreateWebsitePopup = ({
  knowledgeBaseId = null,
  trigger,
  closeSourceModal = () => {},
  disabled = false,
  initialRequest = {},
}: {
  knowledgeBaseId?: number | string | null;
  trigger?: React.ReactNode;
  closeSourceModal?: () => void;
  disabled?: boolean;
  initialRequest?: CollectorInitialRequest;
}) => {
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const [createCollector, { isLoading: isLoadingCreateCollector }] =
    useCreateKnowledgeBaseCollectorMutation();
  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: knowledgeBaseId || id,
        include_data: false,
      },
      {
        skip: !(knowledgeBaseId || id),
      },
    );
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<WebsiteFormValues>({
    defaultValues: {
      urls: [{ value: '' }],
      exclude_urls: [],
      name: '',
      autoSync: !isTrialPlan,
      recursive: true,
      interval: isV1 ? WEBSITE_INTERVAL.MONTHLY : WEBSITE_INTERVAL.WEEKLY,
    },
    resolver: formResolver(
      object({
        name: stringScheme({ required: false }).label('Name'),
        urls: array()
          .of(
            object({
              value: urlScheme({ required: true }).label('URL'),
            }),
          )
          .min(1, 'At least one URL is required'),
        exclude_urls: array().of(
          object({
            value: urlScheme({ required: false }).label('Exclude URL'),
          }),
        ),
        recursive: booleanScheme({ required: false }).label('Sync Nested Pages'),
        autoSync: booleanScheme({ required: false }).label('AutoSync'),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const urlFields = useFieldArray({
    control,
    name: 'urls',
    rules: { minLength: 1 },
  });

  const excludeUrlFields = useFieldArray({
    control,
    name: 'exclude_urls',
  });

  useEffect(() => {
    if (isTrialPlan) {
      reset({
        autoSync: !isTrialPlan,
        recursive: true,
        interval: WEBSITE_INTERVAL.MONTHLY,
        urls: [{ value: '' }],
      });
      urlFields.replace([{ value: '' }]);
      excludeUrlFields.replace([]);
    }
  }, [isTrialPlan]);

  useLoaderWhile(isLoadingCreateCollector, isLoadingCollectors);

  const submit = (data: WebsiteFormValues) => {
    const name = replaceSpecialSymbols(data.urls[0]?.value.trim()) || WEBSITE_INITIAL_REQUEST.name;
    let body: Record<string, unknown> & { schedule: Record<string, unknown> };

    if (isV1) {
      body = {
        ...WEBSITE_INITIAL_REQUEST,
        ...initialRequest,
        // A copy, so setting the schedule below doesn't change the shared default request.
        schedule: { ...(initialRequest.schedule ?? WEBSITE_INITIAL_REQUEST.schedule) },
      };
      body.schedule.enabled = data.autoSync;
      body.schedule.interval = data.interval;
      body.urls = data.urls.map((url) => url.value.trim());
    } else {
      body = {
        ...WEBSITE_INITIAL_REQUEST_V2,
        ...initialRequest,
        schedule: { ...(initialRequest.schedule ?? WEBSITE_INITIAL_REQUEST_V2.schedule) },
      };
      body.schedule.enabled = data.autoSync;
      body.schedule.interval = data.interval;
      body.url = data.urls[0].value.trim();
    }

    if (data?.exclude_urls?.length) {
      body.exclude_urls = data.exclude_urls.map((url) => url.value.trim());
    }

    body.recursive = data.recursive;
    body.name = generateUniqueName(name, collectorsResponse);

    createCollector({ id: knowledgeBaseId || id, body }).then((createCollectorResult) => {
      if (!('error' in createCollectorResult)) {
        notification.success(`${createCollectorResult.data.name} Added Successfully.`);
        closeSourceModal();
        close();
      }
    });
  };

  const close = () => {
    urlFields.replace([{ value: '' }]);
    excludeUrlFields.replace([]);
    isTrialPlan
      ? reset({
          autoSync: !isTrialPlan,
          recursive: true,
          interval: WEBSITE_INTERVAL.MONTHLY,
          urls: [{ value: '' }],
        })
      : reset();
    setOpen(false);
  };

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      return close();
    }

    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent closeButtonHidden className="pb-0">
        <DialogHeader>
          <DialogTitle>Add website</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 pt-4">
          <div className="content">
            <form
              id="create-new-website-form"
              onSubmit={handleSubmit(submit)}
              className={'flex flex-col gap-5'}
            >
              <WebsiteCollectorFields
                control={control}
                urlFields={urlFields}
                excludeUrlFields={excludeUrlFields}
                excludeButtonLabel="Exclude URL"
              />
              <div className="sticky bottom-0 w-full bg-white">
                <div className="flex justify-center my-5">
                  <div className="flex gap-2.5">
                    <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                      Cancel
                    </Button>
                    <Button disabled={!isDirty} type="submit" className="min-w-24">
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
